import {
    getNumberValue,
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import { SaveNoteWritingIcon } from '@apps/web/components/phoebe_chat_view/tool_calls/record_settings_tool_ui';
import {
    ActionMetricGrid,
    DetailChipList,
    formatDetailStatus,
    getStatusBadge,
    getStringArray,
    ToolDetailHeader,
    ToolDetailKeyValue,
    ToolIssueCard,
    ToolTopicSection,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';

export const COMPACT_OUTREACH_SUGGESTION_WRITE_TOOLS = new Set<string>([
    'add_outreach_suggestions',
    'apply_outreach_suggestion_drafts',
]);

export const OUTREACH_ACTION_TOOL_NAMES = new Set<string>([
    'assign_shifts_to_caregiver',
    'draft_agent_outreach',
    'fill_callout_shifts',
    'manage_outreach_add_contact_attempts',
    'manage_outreach_add_suggestions',
    'manage_outreach_cancel',
    'manage_outreach_pause',
    'manage_outreach_remove_suggestions',
    'manage_outreach_resume',
    'manage_outreach_set_status',
    'manage_outreach_start_with_contacts',
]);

export function getArrayCount(value: unknown): number {
    return Array.isArray(value) ? value.length : 0;
}

export function getActionMetric(
    data: Record<string, unknown>,
    keys: ReadonlyArray<string>,
): number | null {
    for (const key of keys) {
        const value = getNumberValue(data, key);
        if (value !== null) {
            return value;
        }
    }
    return null;
}

export function ActionIdList({
    title,
    values,
}: {
    readonly title: string;
    readonly values: ReadonlyArray<unknown>;
}) {
    const stringValues = values
        .filter((value): value is string => typeof value === 'string')
        .slice(0, 4);
    if (stringValues.length === 0) {
        return null;
    }

    return (
        <ToolTopicSection title={title}>
            <div className="space-y-1">
                {stringValues.map((value) => (
                    <p
                        key={value}
                        className="text-muted-foreground truncate font-mono text-[11px]"
                    >
                        {value}
                    </p>
                ))}
            </div>
        </ToolTopicSection>
    );
}

export function getActionCaregiverNames(
    data: Record<string, unknown>,
    args: Record<string, unknown> | null,
): Array<string> {
    const names: Array<string> = [];
    const caregivers = Array.isArray(data['caregivers'])
        ? data['caregivers'].filter(isRecord)
        : [];
    for (const caregiver of caregivers) {
        const name =
            getStringValue(caregiver, 'caregiver_name') ??
            getStringValue(caregiver, 'name');
        if (name) {
            names.push(name);
        }
    }

    const removedCaregivers = getStringArray(data['removed_caregivers']);
    names.push(...removedCaregivers);

    const suggestions = Array.isArray(args?.['suggestions'])
        ? args['suggestions'].filter(isRecord)
        : [];
    for (const suggestion of suggestions) {
        const name = getStringValue(suggestion, 'caregiver_name');
        if (name) {
            names.push(name);
        }
    }

    const directName =
        getStringValue(data, 'caregiver_name') ??
        getStringValue(data, 'assigned_caregiver_name');
    if (directName) {
        names.push(directName);
    }

    return Array.from(new Set(names));
}

export function ActionCaregiverList({
    names,
}: {
    readonly names: ReadonlyArray<string>;
}) {
    if (names.length === 0) {
        return null;
    }

    return (
        <ToolTopicSection title="Caregivers">
            <div className="space-y-1">
                {names.slice(0, 4).map((name) => (
                    <p key={name} className="text-foreground text-xs">
                        {name}
                    </p>
                ))}
            </div>
        </ToolTopicSection>
    );
}

export function DraftOutreachActionCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const shiftIds = Array.isArray(data['shift_ids']) ? data['shift_ids'] : [];
    const status = getStringValue(data, 'status');
    const agentState =
        getStringValue(data, 'agent_run_status') ??
        getStringValue(data, 'outreach_agent_state');
    const outreachType = formatDetailStatus(
        getStringValue(data, 'outreach_type'),
    );

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Outreach created"
                subtitle={outreachType}
                badges={[
                    ...getStatusBadge(status, 'blue'),
                    ...getStatusBadge(agentState, 'neutral'),
                ]}
            />
            <ActionMetricGrid
                metrics={[
                    { label: 'Shifts', value: shiftIds.length },
                    {
                        label: 'Suggestions',
                        value: getActionMetric(data, ['suggestion_count']),
                    },
                    {
                        label: 'Attempts',
                        value: getActionMetric(data, [
                            'contact_attempts_created',
                        ]),
                    },
                ]}
            />
            <ToolTopicSection title="Outreach">
                <ToolDetailKeyValue
                    label="ID"
                    value={
                        getStringValue(data, 'outreach_id') ?? 'Not returned'
                    }
                />
            </ToolTopicSection>
        </div>
    );
}

export function SuggestionMutationActionCard({
    data,
    args,
    action,
}: {
    readonly data: Record<string, unknown>;
    readonly args: Record<string, unknown> | null;
    readonly action: 'add' | 'remove';
}) {
    const status = getStringValue(data, 'status');
    const mode =
        formatDetailStatus(getStringValue(data, 'mode')) ??
        formatDetailStatus(getStringValue(args, 'mode'));
    const names = getActionCaregiverNames(data, args);

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title={
                    action === 'add'
                        ? 'Caregiver suggestions added'
                        : 'Caregiver suggestions removed'
                }
                subtitle={mode ? `Mode: ${mode}` : null}
                badges={getStatusBadge(status, 'green')}
            />
            <ActionMetricGrid
                metrics={[
                    {
                        label: action === 'add' ? 'Added' : 'Removed',
                        value: getActionMetric(data, [
                            'written_count',
                            'removed_count',
                        ]),
                    },
                    {
                        label: 'Remaining',
                        value: getActionMetric(data, ['remaining_count']),
                    },
                ]}
            />
            <ActionCaregiverList names={names} />
        </div>
    );
}

function outreachFilterRuleTitle(status: string | null): string {
    switch (status) {
        case 'removed':
            return 'Phoebe removed a rule';
        case 'no_rule_found':
            return 'No matching rule found';
        case 'already_exists':
        case 'updated':
        case 'added':
        default:
            return 'Phoebe saved a rule';
    }
}

function formatOutreachFilterMode(value: string | null): string | null {
    switch (value?.trim().toLowerCase()) {
        case 'skip':
            return 'Skip';
        case 'allow':
            return 'Allow';
        default:
            return formatDetailStatus(value);
    }
}

export function OutreachFilterRuleActionCard({
    data,
    args,
}: {
    readonly data: Record<string, unknown>;
    readonly args: Record<string, unknown> | null;
}) {
    const status = getStringValue(data, 'status');
    const outreachType =
        formatDetailStatus(getStringValue(data, 'outreach_type')) ??
        formatDetailStatus(getStringValue(args, 'outreach_type'));
    const entityType =
        formatDetailStatus(getStringValue(data, 'entity_type')) ??
        formatDetailStatus(getStringValue(args, 'entity_type'));
    const filterMode =
        formatOutreachFilterMode(getStringValue(data, 'filter_mode')) ??
        formatOutreachFilterMode(getStringValue(args, 'filter_mode'));
    const ruleDetails = [outreachType, entityType, filterMode]
        .filter((value): value is string => value !== null)
        .join(' · ');

    return (
        <div className="space-y-2">
            <div className="flex items-start gap-1.5">
                <SaveNoteWritingIcon />
                <ToolDetailHeader
                    title={outreachFilterRuleTitle(status)}
                    subtitle={ruleDetails || null}
                    badges={getStatusBadge(status, 'green')}
                />
            </div>
            <ToolTopicSection title="Rule">
                <div className="space-y-1.5">
                    <ToolDetailKeyValue
                        label="ID"
                        value={
                            getStringValue(data, 'rule_id') ?? 'Not returned'
                        }
                    />
                    <ToolDetailKeyValue
                        label="Entity"
                        value={
                            getStringValue(data, 'entity_id') ??
                            getStringValue(args, 'entity_id') ??
                            'Not returned'
                        }
                    />
                </div>
            </ToolTopicSection>
        </div>
    );
}

export function OutreachStatusActionCard({
    data,
    args,
}: {
    readonly data: Record<string, unknown>;
    readonly args: Record<string, unknown> | null;
}) {
    const action =
        formatDetailStatus(getStringValue(data, 'action')) ??
        formatDetailStatus(getStringValue(args, 'action'));
    const fromStatus = formatDetailStatus(getStringValue(data, 'from_status'));
    const toStatus =
        formatDetailStatus(getStringValue(data, 'outreach_status')) ??
        formatDetailStatus(getStringValue(data, 'status'));

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Outreach status updated"
                subtitle={action}
                badges={toStatus ? [{ label: toStatus, tone: 'blue' }] : []}
            />
            <ToolTopicSection title="Transition">
                <div className="space-y-1.5">
                    <ToolDetailKeyValue
                        label="From"
                        value={fromStatus ?? 'Not returned'}
                    />
                    <ToolDetailKeyValue
                        label="To"
                        value={toStatus ?? 'Not returned'}
                    />
                </div>
            </ToolTopicSection>
        </div>
    );
}

export function ContactAttemptsActionCard({
    data,
    started,
}: {
    readonly data: Record<string, unknown>;
    readonly started: boolean;
}) {
    const status =
        getStringValue(data, 'status') ??
        (started ? 'in_progress' : getStringValue(data, 'outreach_status'));
    const channels = getStringArray(data['channels']);

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title={started ? 'Outreach started' : 'Contact attempts queued'}
                subtitle={getStringValue(data, 'outreach_id')}
                badges={getStatusBadge(status, started ? 'blue' : 'green')}
            />
            <ActionMetricGrid
                metrics={[
                    {
                        label: 'Attempts',
                        value: getActionMetric(data, [
                            'contact_attempts_created',
                            'attempt_count',
                        ]),
                    },
                    {
                        label: 'Caregivers',
                        value: getActionMetric(data, ['caregiver_count']),
                    },
                    {
                        label: 'Shifts',
                        value: getActionMetric(data, ['shift_count']),
                    },
                ]}
            />
            {channels.length > 0 ? (
                <ToolTopicSection title="Channels">
                    <DetailChipList values={channels} />
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

export function FillCalloutShiftsActionCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const succeededShiftIds = Array.isArray(data['succeeded_shift_ids'])
        ? data['succeeded_shift_ids']
        : [];
    const notAttemptedShiftIds = Array.isArray(data['not_attempted_shift_ids'])
        ? data['not_attempted_shift_ids']
        : [];
    const failedCount =
        getArrayCount(data['failed_shift_ids']) +
        (data['failed_shift'] ? 1 : 0);

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Callout shifts filled"
                subtitle={getStringValue(data, 'assigned_caregiver_name')}
                badges={getStatusBadge(getStringValue(data, 'status'), 'green')}
            />
            <ActionMetricGrid
                metrics={[
                    { label: 'Filled', value: succeededShiftIds.length },
                    {
                        label: 'Not attempted',
                        value: notAttemptedShiftIds.length,
                    },
                    { label: 'Failed', value: failedCount },
                ]}
            />
            <ActionIdList title="Filled shifts" values={succeededShiftIds} />
            <ActionIdList title="Not attempted" values={notAttemptedShiftIds} />
        </div>
    );
}

export function AssignShiftsActionCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const assignedShiftIds = Array.isArray(data['assigned_shift_ids'])
        ? data['assigned_shift_ids']
        : [];
    const alreadyAssignedShiftIds = Array.isArray(
        data['already_assigned_shift_ids'],
    )
        ? data['already_assigned_shift_ids']
        : [];
    const reassignedShiftIds = Array.isArray(data['reassigned_shift_ids'])
        ? data['reassigned_shift_ids']
        : [];
    const hasError = getStringValue(data, 'error') !== null;

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title={hasError ? 'Assignment needs review' : 'Shifts assigned'}
                subtitle={getStringValue(data, 'caregiver_name')}
                badges={
                    hasError
                        ? [{ label: 'Needs review' }]
                        : getStatusBadge(
                              getStringValue(data, 'status'),
                              'green',
                          )
                }
            />
            {hasError ? (
                <ToolIssueCard
                    title="Review needed"
                    error={getStringValue(data, 'error') ?? ''}
                />
            ) : null}
            <ActionMetricGrid
                metrics={[
                    { label: 'Assigned', value: assignedShiftIds.length },
                    {
                        label: 'Already assigned',
                        value: alreadyAssignedShiftIds.length,
                    },
                    { label: 'Reassigned', value: reassignedShiftIds.length },
                ]}
            />
            <ActionIdList title="Assigned shifts" values={assignedShiftIds} />
        </div>
    );
}
